import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Video, Share2, CheckSquare, Info, RefreshCw } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import { toast } from 'react-hot-toast';
import Loader from './Loader';
import ShareMeetingModal from './ShareMeetingModal';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import DOMPurify from 'dompurify';
import { useTheme } from '../contexts/ThemeContext';

const MeetingDetail = () => {
  const { meetingId, shareToken } = useParams();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();
  
  const [meeting, setMeeting] = useState(null);
  const [activeTab, setActiveTab] = useState('notes');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleProcessTranscript = useCallback(async () => {
    if (!meeting?.transcript || meeting?.status !== 'pending') return;
    
    setIsProcessing(true);
    try {
      const response = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/process-meeting-transcript',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            transcript: meeting.transcript,
            meetingId: meeting.id
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to process transcript');
      }

      const data = await response.json();
      setMeeting(prev => ({
        ...prev,
        processed_transcript: data,
        status: 'processed'
      }));
      toast.success('Transcript processed successfully');
    } catch (error) {
      console.error('Error processing transcript:', error);
      toast.error('Failed to process transcript');
    } finally {
      setIsProcessing(false);
    }
  }, [meeting, authenticatedFetch]);

  // Add Markdown components configuration
  const markdownComponents = {
    h1: ({ node, ...props }) => (
      <h1 className="text-3xl font-bold my-4 text-theme" {...props} />
    ),
    h2: ({ node, ...props }) => (
      <h2 className="text-2xl font-bold my-4 text-theme" {...props} />
    ),
    h3: ({ node, ...props }) => (
      <h3 className="text-xl font-bold my-4 text-theme" {...props} />
    ),
    p: ({ node, ...props }) => (
      <p className="my-2 text-theme" {...props} />
    ),
    ul: ({ node, ...props }) => (
      <ul className="list-disc list-inside my-2 text-theme" {...props} />
    ),
    ol: ({ node, ...props }) => (
      <ol className="list-decimal list-inside my-2 text-theme" {...props} />
    ),
    li: ({ node, ...props }) => (
      <li className="my-1 text-theme" {...props} />
    ),
    strong: ({ node, ...props }) => (
      <strong className="font-bold text-theme" {...props} />
    ),
    blockquote: ({ node, ...props }) => (
      <blockquote className="border-l-4 border-gray-300 pl-4 my-2 italic text-theme" {...props} />
    ),
    code: ({ node, inline, ...props }) => (
      inline ? (
        <code className="bg-gray-100 rounded px-1 text-gray-800" {...props} />
      ) : (
        <code className="block bg-gray-100 p-2 rounded my-2 overflow-x-auto text-gray-800" {...props} />
      )
    )
  };

  useEffect(() => {
    let isMounted = true;

    const fetchMeeting = async () => {
      if (!isLoaded || !isSignedIn) return;
      
      try {
        const url = shareToken
          ? `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/meetings/share/${shareToken}/view`
          : `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/meetings/${meetingId}`;

        const response = await authenticatedFetch(url);
        
        if (!response.ok) {
          throw new Error('Failed to fetch meeting');
        }

        const data = await response.json();
        
        // Parse the processed_transcript if it's a string
        if (data.processed_transcript && typeof data.processed_transcript === 'string') {
          data.processed_transcript = JSON.parse(data.processed_transcript);
        }
        
        if (isMounted) {
          setMeeting(data);
          // Only auto-process if not using share token and status is pending and hasn't been processed yet
          if (data.status === 'pending' && !shareToken && !data.processed_transcript) {
            handleProcessTranscript();
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching meeting:', error);
          setError('Failed to load meeting');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchMeeting();

    return () => {
      isMounted = false;
    };
  }, [meetingId, shareToken, isLoaded, isSignedIn]);

  const handleAddToAccount = async () => {
    if (!shareToken) return;
    
    setIsAdding(true);
    try {
      const response = await authenticatedFetch(
        `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/meetings/${shareToken}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ shareToken })
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success('Meeting added to your account');
        navigate(`/meetings/${data.id}`);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to add meeting');
      }
    } catch (error) {
      console.error('Error adding meeting:', error);
      toast.error('Failed to add meeting');
    } finally {
      setIsAdding(false);
    }
  };

  if (!isLoaded) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <p>{error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="p-4 text-gray-500">
        Meeting not found
      </div>
    );
  }

  const tabs = [
    { id: 'notes', label: 'Processed Notes', icon: <FileText size={20} /> },
    { id: 'transcript', label: 'Raw Transcript', icon: <Video size={20} /> }
  ];

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-theme theme-transition">
      <header className="flex-shrink-0 border-b border-theme">
        <div className="h-full px-4 py-4 sm:px-6 lg:px-8">
          <nav className="flex items-center text-sm select-text overflow-x-auto whitespace-nowrap pb-1">
            <button
              onClick={() => navigate('/meetings')}
              className="text-accent hover:text-theme hover:underline"
            >
              Meetings
            </button>
            <span className="mx-2 text-accent">/</span>
            <span className="text-theme truncate max-w-[200px] inline-block">
              {meeting.title}
            </span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-4">
            <div className="select-text">
              <h1 className="text-2xl font-bold text-theme truncate">
                {meeting.title}
              </h1>
              <p className="text-sm text-accent">
                {new Date(meeting.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {meeting.status === 'pending' && !shareToken && (
                <button
                  onClick={handleProcessTranscript}
                  disabled={isProcessing}
                  className="px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02] w-full sm:w-auto"
                  style={{ 
                    opacity: isProcessing ? 0.5 : 1,
                    cursor: isProcessing ? 'not-allowed' : 'pointer'
                  }}
                >
                  <RefreshCw size={18} className={isProcessing ? 'animate-spin' : ''} />
                  <span className="whitespace-nowrap">{isProcessing ? 'Processing...' : 'Process Transcript'}</span>
                </button>
              )}
              {shareToken ? (
                <button
                  onClick={handleAddToAccount}
                  disabled={isAdding}
                  className="px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02] w-full sm:w-auto"
                  style={{ 
                    opacity: isAdding ? 0.5 : 1,
                    cursor: isAdding ? 'not-allowed' : 'pointer'
                  }}
                >
                  <CheckSquare size={18} />
                  <span className="whitespace-nowrap">{isAdding ? 'Adding...' : 'Add to My Account'}</span>
                </button>
              ) : (
                <button
                  onClick={() => setShowShareModal(true)}
                  className="px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02] w-full sm:w-auto"
                >
                  <Share2 size={18} />
                  <span className="whitespace-nowrap">Share</span>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2 overflow-x-auto mt-6 pb-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-4 py-2 rounded-lg flex items-center gap-2 transition-all whitespace-nowrap
                  ${activeTab === tab.id 
                    ? 'bg-primary text-theme' 
                    : 'bg-secondary text-accent hover:bg-primary-hover'}
                `}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          {activeTab === 'notes' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 order-2 lg:order-1">
                <div className="p-4 sm:p-6 rounded-lg bg-theme border border-theme theme-transition select-text">
                  <div className="prose max-w-none text-theme">
                    {meeting.processed_transcript?.content ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkMath]}
                        rehypePlugins={[rehypeKatex, rehypeRaw]}
                        components={markdownComponents}
                      >
                        {DOMPurify.sanitize(meeting.processed_transcript.content)}
                      </ReactMarkdown>
                    ) : (
                      <div className="text-accent">
                        No processed notes available for this meeting.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4 sm:space-y-6 order-1 lg:order-2">
                <div className="p-4 sm:p-6 rounded-lg bg-theme border border-theme theme-transition select-text">
                  <div className="flex items-center gap-2 mb-4">
                    <Info size={20} className="text-accent flex-shrink-0" />
                    <h3 className="text-lg font-medium text-theme">Summary</h3>
                  </div>
                  <p className="text-theme">
                    {meeting.processed_transcript?.summary || 'No summary available.'}
                  </p>
                </div>

                <div className="p-4 sm:p-6 rounded-lg bg-theme border border-theme theme-transition select-text">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckSquare size={20} className="text-accent flex-shrink-0" />
                    <h3 className="text-lg font-medium text-theme">Action Items</h3>
                  </div>
                  {meeting.processed_transcript?.action_items?.length > 0 ? (
                    <ul className="space-y-4">
                      {meeting.processed_transcript.action_items.map((item, index) => (
                        <li key={index} className="flex gap-4">
                          <div className="flex-1">
                            <p className="font-medium text-theme">{item.item}</p>
                            {item.assignee && (
                              <p className="text-sm text-theme">
                                Assignee: {item.assignee}
                              </p>
                            )}
                            {item.deadline && (
                              <p className="text-sm text-theme">
                                Due: {item.deadline}
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-theme">No action items found.</p>
                  )}
                </div>

                {meeting.audio_url && (
                  <div className="p-4 sm:p-6 rounded-lg bg-theme border border-theme theme-transition">
                    <div className="flex items-center gap-2 mb-4">
                      <FileText size={20} className="text-accent flex-shrink-0" />
                      <h3 className="text-lg font-medium text-theme">Meeting Audio</h3>
                    </div>
                    <audio 
                      className="w-full" 
                      controls
                      src={meeting.audio_url}
                      controlsList="nodownload"
                    >
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-4 sm:p-6 rounded-lg bg-theme border border-theme theme-transition select-text">
              <div className="whitespace-pre-wrap text-theme">
                {meeting.transcript || (
                  <div className="text-theme">
                    No transcript available for this meeting.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <ShareMeetingModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        meetingId={meetingId}
        title={meeting?.title}
      />
    </div>
  );
};

export default MeetingDetail; 